import { parse, stringify } from 'query-string';
import difference from 'lodash.difference';
import intersection from 'lodash.intersection';
import dayjs from 'utils/dayjs';

import * as defaultValues from './defaultValues';

const CURRENCY_REGEXP = /^[A-Za-z0-9]*$/;
const CONVERTER_TYPE_REGEXP = /(midbank|bestbank|auction|nbu|contractsBank)$/;
const VALUE_REGEXP = /^\d+(\.)?(\d+)?$/;
const PRIORITY_REGEXP = /^(0|1)$/;
const DATE_REGEXP = /^[0-3][0-9]-[0-1][0-9]-[1-2][0-9]{3}$/;
const CURRENCIES_LIST_REGEXP = /^[A-Za-z0-9-]+$/;

const correctDate = date => {
  const [year, month, day] = date
    .split('-')
    .reverse()
    .map(value => parseInt(value, 10));

  return new Date(year, month - 1, day);
};

export const ROUTER_PARAMS_CONFIG = {
  sourceCurrency: {
    urlName: 'sourceCurrency',
    location: 'pathname',

    getFormattedValue: value => {
      if (!value || !CURRENCY_REGEXP.test(value)) {
        return defaultValues.DEFAULT_SOURCE_CURRENCY;
      }

      return value;
    },

    getFormattedURLValue: value => {
      if (!value || !CURRENCY_REGEXP.test(value)) {
        return '';
      }

      return value;
    },
  },

  targetCurrency: {
    urlName: 'targetCurrency',
    location: 'pathname',

    getFormattedValue: value => {
      if (!value || !CURRENCY_REGEXP.test(value)) {
        return defaultValues.DEFAULT_TARGET_CURRENCY;
      }

      return value;
    },

    getFormattedURLValue: value => {
      if (!value || !CURRENCY_REGEXP.test(value)) {
        return '';
      }

      return value;
    },
  },

  sourceType: {
    urlName: 'sourceType',
    location: 'pathname',

    getFormattedValue: value => {
      if (!value || value !== '-c') {
        return defaultValues.DEFAULT_CURRENCY_TYPE;
      }

      return 'crypto';
    },

    getFormattedURLValue: value => {
      if (!value || value === 'money') {
        return '';
      }

      return 'crypto';
    },
  },

  targetType: {
    urlName: 'targetType',
    location: 'pathname',

    getFormattedValue: value => {
      if (!value || value !== '-c') {
        return defaultValues.DEFAULT_CURRENCY_TYPE;
      }

      return 'crypto';
    },

    getFormattedURLValue: value => {
      if (!value || value === 'money') {
        return '';
      }

      return 'crypto';
    },
  },

  converterType: {
    urlName: 'converter-type',
    location: 'search',

    getFormattedValue: value => {
      if (!value || !CONVERTER_TYPE_REGEXP.test(value)) {
        return defaultValues.DEFAULT_CONVERTER_TYPE;
      }

      return value;
    },

    getFormattedURLValue: value => {
      if (!value || !CONVERTER_TYPE_REGEXP.test(value)) {
        return '';
      }

      return value;
    },
  },

  sourceValue: {
    urlName: 'val1',
    location: 'search',

    getFormattedValue: value => {
      if (value === '') {
        return '';
      }

      if (!VALUE_REGEXP.test(value) || value === undefined) {
        return defaultValues.DEFAULT_VALUE;
      }

      return parseFloat(value);
    },

    getFormattedURLValue: (value, showDefaultValueInURL) => {
      if (
        !VALUE_REGEXP.test(value) ||
        (!showDefaultValueInURL &&
          parseFloat(value) === defaultValues.DEFAULT_VALUE)
      ) {
        return '';
      }

      return value;
    },
  },

  targetValue: {
    urlName: 'val2',
    location: 'search',

    getFormattedValue: value => {
      if (value === '') {
        return '';
      }

      if (!VALUE_REGEXP.test(value) || value === undefined) {
        return defaultValues.DEFAULT_VALUE;
      }

      return parseFloat(value);
    },

    getFormattedURLValue: (value, showDefaultValueInURL) => {
      if (
        !VALUE_REGEXP.test(value) ||
        (!showDefaultValueInURL &&
          parseFloat(value) === defaultValues.DEFAULT_VALUE)
      ) {
        return '';
      }

      return value;
    },
  },

  priority: {
    urlName: 'priority',
    location: 'search',

    getFormattedValue: value => {
      if (!PRIORITY_REGEXP.test(value)) {
        return defaultValues.DEFAULT_PRIORITY;
      }

      return parseFloat(value);
    },

    getFormattedURLValue: (value, showDefaultValueInURL) => {
      if (
        !PRIORITY_REGEXP.test(value) ||
        (!showDefaultValueInURL &&
          parseInt(value, 10) === defaultValues.DEFAULT_PRIORITY)
      ) {
        return '';
      }

      return parseInt(value, 10);
    },
  },

  date: {
    urlName: 'date',
    location: 'search',

    getFormattedValue: value => {
      const defaultDate = dayjs().format(defaultValues.DEFAULT_API_DATE_FORMAT);

      if (!DATE_REGEXP.test(value)) {
        return defaultDate;
      }

      const date = correctDate(value);

      if (dayjs(date).isAfter(dayjs().endOf('day'))) {
        return defaultDate;
      }

      return dayjs(date).format(defaultValues.DEFAULT_API_DATE_FORMAT);
    },

    getFormattedURLValue: value => {
      if (!DATE_REGEXP.test(value)) {
        return '';
      }

      return value;
    },
  },

  currenciesList: {
    urlName: 'currencies-list',
    location: 'search',

    getFormattedValue: value => {
      if (!CURRENCIES_LIST_REGEXP.test(value) || value === undefined) {
        return [];
      }

      return value.split('-');
    },

    getFormattedURLValue: value => {
      if (!DATE_REGEXP.test(value)) {
        return '';
      }

      return value;
    },
  },
};

const groupBy = (array, key) =>
  array.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item],
    }),
    {}
  );

const removeEmptyField = object =>
  Object.keys(object).reduce(
    (res, key) =>
      object[key] !== undefined && object[key] !== null && object[key] !== ''
        ? { ...res, [key]: object[key] }
        : res,
    {}
  );

const getValue = ({
  name,
  value = '',
  type,
  showDefaultValueInURL = false,
  currentValue,
  multiple,
}) => {
  if (!name && (value === undefined || value === null)) {
    return null;
  }

  const {
    [name]: { getFormattedURLValue, getFormattedValue },
  } = ROUTER_PARAMS_CONFIG;

  const formattedValue = getFormattedURLValue(value, showDefaultValueInURL);

  const currentValueArray = getFormattedValue(currentValue);

  if (!multiple || type === 'pathname') {
    return formattedValue;
  }

  if (Array.isArray(formattedValue)) {
    const intersectingValues = intersection(formattedValue, currentValueArray);

    if (intersectingValues.length === 0) {
      return [...currentValueArray, ...formattedValue];
    }

    if (formattedValue.length > intersectingValues.length) {
      return Array.from(
        new Set([
          ...currentValueArray,
          ...formattedValue,
          ...intersectingValues,
        ])
      );
    }

    return difference(currentValueArray, intersectingValues);
  }

  if (currentValueArray.includes(formattedValue)) {
    return currentValueArray.filter(item => item !== formattedValue);
  }

  return [...currentValueArray, formattedValue];
};

const getPathnameByParams = (params, pathnameSeparator) =>
  Object.keys(params)
    .sort((a, b) => {
      const leftOperandOrder = (ROUTER_PARAMS_CONFIG[a] || {}).urlPlaceOrder;
      const rightOperandOrder = (ROUTER_PARAMS_CONFIG[b] || {}).urlPlaceOrder;
      const leftOperand =
        typeof leftOperandOrder === 'number' ? leftOperandOrder : Infinity;
      const rightOperand =
        typeof rightOperandOrder === 'number' ? rightOperandOrder : Infinity;

      return leftOperand - rightOperand;
    })
    .reduce((result, paramName, i, arr) => {
      if (!params[paramName]) {
        return result;
      }

      return `${result}${params[paramName]}${
        i === arr.length - 1 ? '/' : pathnameSeparator
      }`;
    }, '/');

const getNecessaryConfigs = (
  newValueConfig,
  configs,
  searchState,
  pathState,
  multiple
) =>
  (configs || []).filter(
    ({
      name: toggleName,
      type: toggleType,
      searchingValue,
      notSearchingValue,
      substitutionName = newValueConfig.name,
      substitutionValue,
      substitutionType = newValueConfig.type,
    }) => {
      const {
        [toggleName || '']: { urlName: toggleURLName } = {},
      } = ROUTER_PARAMS_CONFIG;

      const state = toggleType === 'pathname' ? pathState : searchState;

      if (
        !toggleURLName ||
        (searchingValue && state[toggleURLName] !== searchingValue) ||
        (notSearchingValue && state[toggleURLName] === notSearchingValue) ||
        (!multiple &&
          substitutionValue &&
          newValueConfig.name === substitutionName &&
          newValueConfig.value === substitutionValue &&
          newValueConfig.type === substitutionType)
      ) {
        return false;
      }

      return true;
    }
  );

const getSubstitutions = (toggleConfigs, newValue) =>
  toggleConfigs.reduce(
    (
      subs,
      {
        substitutionName = newValue.name,
        substitutionValue,
        substitutionType = newValue.type,
      }
    ) =>
      substitutionValue &&
      (newValue.name !== substitutionName ||
        newValue.value !== substitutionValue ||
        newValue.type !== substitutionType)
        ? [
            ...subs,
            {
              name: substitutionName,
              value: substitutionValue,
              type: substitutionType,
            },
          ]
        : subs,
    []
  );

const getToggleValues = (configs, showDefaultValueInURL) =>
  (configs || []).reduce(
    (values, { name: toggleName, value: toggleValue, type: toggleType }) => {
      const {
        [toggleName || '']: { urlName: toggleURLName } = {},
      } = ROUTER_PARAMS_CONFIG;

      const toggleUrlValue = getValue({
        name: toggleName,
        value: toggleValue,
        type: toggleType,
        showDefaultValueInURL,
      });

      return { ...values, [toggleURLName]: toggleUrlValue };
    },
    {}
  );

const checkIsUrlValueShouldChange = (
  newValue,
  equalParam,
  searchState,
  pathState,
  toggleConfigs
) => {
  const isExistInToogle = toggleConfigs.some(
    ({
      name,
      type,
      value,
      searchingValue,
      notSearchingValue,
      substitutionName = newValue.name,
      substitutionValue,
      substitutionType = newValue.type,
    }) => {
      const { [name || '']: { urlName } = {} } = ROUTER_PARAMS_CONFIG;

      const state = type === 'pathname' ? pathState : searchState;

      return (
        ((searchingValue && state[urlName] === searchingValue) ||
          (notSearchingValue && state[urlName] !== notSearchingValue)) &&
        state[urlName] !== value &&
        newValue.name === substitutionName &&
        newValue.value === substitutionValue &&
        newValue.type === substitutionType
      );
    }
  );

  const isEqual =
    !equalParam ||
    (equalParam.type === 'search' &&
      searchState[equalParam.name] !== equalParam.value) ||
    (equalParam.type === 'pathname' &&
      pathState[equalParam.name] !== equalParam.value);

  return !isExistInToogle && isEqual;
};

const mergeSearchWithSubstitutions = (
  search = {},
  substitutions = {},
  multiple
) => {
  if (!multiple) {
    return { ...substitutions, ...search };
  }

  return Object.keys(substitutions).reduce((res, key) => {
    if (res[key]) {
      res[key] = Array.isArray(res[key])
        ? [...res[key], substitutions[key]]
        : [res[key], substitutions[key]];
    } else {
      res[key] = [substitutions[key]];
    }

    res[key] = res[key].filter(
      value => value !== undefined && value !== null && value !== ''
    );

    return res;
  }, search);
};

export const getLink = ({
  name,
  value,
  type,
  multiple,
  location,
  match,
  toggle = [],
  equalParam,
  showDefaultValueInURL,
  pathnameSeparator = '/',
}) => {
  if (!location || !match) {
    throw new Error('Location and match parameters is required');
  }

  if (!name) {
    return `${location.pathname}${location.search}`;
  }

  const newValueConfig = { name, value, type };
  const locationSearchState = parse(location.search);
  const locationPathState = { ...match.params };
  const toggleConfigs = Array.isArray(toggle) ? toggle : [toggle];

  const {
    [name]: { urlName },
  } = ROUTER_PARAMS_CONFIG;

  const currentValue =
    type === 'search'
      ? locationSearchState[urlName]
      : locationPathState[urlName];

  const shouldChangeUrlValue = checkIsUrlValueShouldChange(
    newValueConfig,
    equalParam,
    locationSearchState,
    locationPathState,
    toggleConfigs
  );

  const newURLValue = shouldChangeUrlValue
    ? getValue({
        name,
        value,
        type,
        showDefaultValueInURL,
        multiple,
        currentValue,
      })
    : currentValue;

  const necessaryToggleConfigs = getNecessaryConfigs(
    newValueConfig,
    toggleConfigs,
    locationSearchState,
    locationPathState,
    multiple
  );

  const substitutions = getSubstitutions(
    necessaryToggleConfigs,
    newValueConfig
  );

  const {
    search: substitutionSearchConfigs,
    pathname: substitutionPathnameConfigs,
  } = groupBy(substitutions, 'type');

  const { pathname: pathnameConfigs, search: searchConfigs } = groupBy(
    necessaryToggleConfigs,
    'type'
  );

  const substitutionSearchUrlValues = getToggleValues(
    substitutionSearchConfigs,
    showDefaultValueInURL
  );

  const substitutionPathnameUrlValues = getToggleValues(
    substitutionPathnameConfigs,
    showDefaultValueInURL
  );

  const toggleSearchUrlValues = getToggleValues(
    searchConfigs,
    showDefaultValueInURL
  );

  const togglePathnameUrlValues = getToggleValues(
    pathnameConfigs,
    showDefaultValueInURL
  );

  const newSearchParam = type === 'search' ? { [urlName]: newURLValue } : {};

  const newLocationSearchState = stringify(
    removeEmptyField({
      ...locationSearchState,
      ...mergeSearchWithSubstitutions(
        newSearchParam,
        substitutionSearchUrlValues,
        multiple
      ),
      ...(toggleSearchUrlValues || {}),
    }),
    { arrayFormat: 'comma' }
  );

  const newLocationSearch = newLocationSearchState
    ? `?${newLocationSearchState}`
    : '';

  const newLocationParamsState = removeEmptyField({
    ...locationPathState,
    ...(type === 'pathname' ? { [urlName]: newURLValue } : {}),
    ...(togglePathnameUrlValues || {}),
    ...(substitutionPathnameUrlValues || {}),
  });

  const newLocationPathname = getPathnameByParams(
    newLocationParamsState,
    pathnameSeparator
  );

  return `${newLocationPathname}${newLocationSearch}`;
};

export const getUrlByLocation = location =>
  `${location.pathname}${location.search}`;
